/**
 * CORE-1200 — код корреляции попытки входа: генерация, разбор маршрута, имя
 * заголовка.
 *
 * Живёт отдельно от `authDiagnostics.ts` намеренно. Заголовок нужен в
 * `services/api.ts`, а `authDiagnostics` тянет за собой Sentry и очередь
 * телеметрии — импортировать всё это в клиент API ради одной строки значило бы
 * поднимать телеметрию раньше, чем сделан первый запрос. Здесь нет ни одного
 * импорта, и модуль остаётся чисто вычислимым: его легко звать из тестов.
 */

/** Заголовок, которым код едет на сервер. Бэк логирует его как есть. */
export const AUTH_CORRELATION_HEADER = 'X-Correlation-Id';

/**
 * Алфавит кода — Crockford base32 без `I`, `L`, `O`, `U`. Код человек диктует
 * голосом или переписывает с экрана, и `0/O`, `1/I/L` в этом месте стоят дороже,
 * чем четыре лишних символа энтропии.
 */
const CODE_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
const CODE_HALF = 5;

function randomInt(max: number): number {
  const g = globalThis as unknown as { crypto?: Crypto };
  if (g.crypto?.getRandomValues) {
    const buf = new Uint32Array(1);
    g.crypto.getRandomValues(buf);
    return buf[0] % max;
  }
  return Math.floor(Math.random() * max);
}

/**
 * `A7K2X-9QF3M` — две группы по пять, чтобы читалось вслух.
 *
 * 50 бит: столкновений в пределах инцидента не бывает, а искать в логах по
 * такому коду можно буквальным поиском подстроки.
 */
export function newCorrelationCode(): string {
  let out = '';
  for (let i = 0; i < CODE_HALF * 2; i++) {
    if (i === CODE_HALF) out += '-';
    out += CODE_ALPHABET[randomInt(CODE_ALPHABET.length)];
  }
  return out;
}

/** Похоже ли на наш код — для валидации на бэке и в тестах. */
export const AUTH_CORRELATION_PATTERN = /^[0-9A-HJKMNP-TV-Z]{5}-[0-9A-HJKMNP-TV-Z]{5}$/;

/**
 * Live/room id из маршрута возврата. Оба входа в Live кладут туда путь:
 * `/live-session/join/:id` и `/practice-rooms/session/:code`; `/room/:id` и
 * `/live/:id` — их публичные ссылки.
 */
export function liveIdFromRoute(route: string | null | undefined): string | null {
  if (!route) return null;
  const m = route.match(
    /\/(?:live-session\/join|practice-rooms\/session|room|live)\/([A-Za-z0-9_-]+)/,
  );
  return m ? m[1] : null;
}

/**
 * Путь без query и хэша.
 *
 * Query здесь режется не для красоты: в `returnTo` попадают ссылки-приглашения,
 * а токен приглашения — секрет, и в телеметрии ему не место (CORE-1038 — ровно
 * такой секрет однажды утёк в Sentry внутри пути).
 */
export function sanitizeRoute(route: string | null | undefined): string | null {
  if (!route) return null;
  const cut = route.split(/[?#]/)[0];
  return cut ? cut.slice(0, 200) : null;
}
